import { MATCHERS, type Condition, type MatcherName } from "@spoonity/flags-contract";

/** Every matcher's value shape (spec/SPEC.md §5), used to build a fresh condition on matcher change. */
export function defaultConditionForMatcher(matcher: MatcherName): Condition {
  switch (matcher) {
    case "in_segment":
    case "not_in_segment":
      return { matcher, attribute: "key", value: "" };
    case "equals":
    case "not_equals":
      return { matcher, attribute: "", value: "" };
    case "in_list":
    case "not_in_list":
      return { matcher, attribute: "", values: [] };
    case "starts_with":
    case "ends_with":
    case "contains":
      return { matcher, attribute: "", value: "" };
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return { matcher, attribute: "", value: 0 };
    case "between":
      return { matcher, attribute: "", min: 0, max: 0 };
    case "semver_eq":
    case "semver_gte":
    case "semver_lte":
      return { matcher, attribute: "", value: "1.0.0" };
    case "semver_between":
      return { matcher, attribute: "", min: "1.0.0", max: "1.0.0" };
    case "is_set":
    case "is_true":
    case "is_false":
      return { matcher, attribute: "" };
    case "list_contains_any":
    case "list_contains_all":
      return { matcher, attribute: "", values: [] };
    default:
      return { matcher: "is_set", attribute: "" };
  }
}

function parseCsvValues(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ConditionRow({
  condition,
  segmentNames,
  onChange,
  onRemove,
  readOnly,
}: {
  condition: Condition;
  segmentNames: string[];
  onChange: (next: Condition) => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const isSegment = condition.matcher === "in_segment" || condition.matcher === "not_in_segment";

  return (
    <div className="condition-row">
      <select
        className="select"
        value={condition.matcher}
        disabled={readOnly}
        aria-label="matcher"
        onChange={(e) => onChange(defaultConditionForMatcher(e.target.value as MatcherName))}
      >
        {MATCHERS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>

      {isSegment ? (
        <input className="select" value="key" disabled aria-label="attribute" />
      ) : (
        <input
          className="select"
          value={condition.attribute}
          disabled={readOnly}
          placeholder="attribute"
          aria-label="attribute"
          onChange={(e) => onChange({ ...condition, attribute: e.target.value } as Condition)}
        />
      )}

      <ValueInput condition={condition} segmentNames={segmentNames} onChange={onChange} readOnly={readOnly} />

      {!readOnly && (
        <button type="button" className="btn small danger" onClick={onRemove} aria-label="Remove condition">
          Remove
        </button>
      )}
    </div>
  );
}

function ValueInput({
  condition,
  segmentNames,
  onChange,
  readOnly,
}: {
  condition: Condition;
  segmentNames: string[];
  onChange: (next: Condition) => void;
  readOnly?: boolean;
}) {
  switch (condition.matcher) {
    case "in_segment":
    case "not_in_segment":
      return (
        <select
          className="select"
          value={condition.value}
          disabled={readOnly}
          aria-label="value"
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
        >
          <option value="" disabled>
            choose a segment…
          </option>
          {segmentNames.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      );
    case "equals":
    case "not_equals": {
      const value = condition.value;
      const kind = typeof value === "boolean" ? "boolean" : typeof value === "number" ? "number" : "string";
      return (
        <div className="row">
          <select
            className="select"
            value={kind}
            disabled={readOnly}
            aria-label="value type"
            onChange={(e) => {
              const nextKind = e.target.value;
              const coerced = nextKind === "number" ? 0 : nextKind === "boolean" ? true : "";
              onChange({ ...condition, value: coerced });
            }}
          >
            <option value="string">string</option>
            <option value="number">number</option>
            <option value="boolean">boolean</option>
          </select>
          {kind === "boolean" ? (
            <select
              className="select"
              value={String(value)}
              disabled={readOnly}
              aria-label="value"
              onChange={(e) => onChange({ ...condition, value: e.target.value === "true" })}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : (
            <input
              className="select"
              type={kind === "number" ? "number" : "text"}
              value={value as string | number}
              disabled={readOnly}
              aria-label="value"
              onChange={(e) =>
                onChange({ ...condition, value: kind === "number" ? Number(e.target.value) : e.target.value })
              }
            />
          )}
        </div>
      );
    }
    case "in_list":
    case "not_in_list":
    case "list_contains_any":
    case "list_contains_all":
      return (
        <input
          className="select"
          type="text"
          value={condition.values.join(", ")}
          disabled={readOnly}
          placeholder="comma-separated values"
          aria-label="values"
          onChange={(e) => onChange({ ...condition, values: parseCsvValues(e.target.value) })}
        />
      );
    case "starts_with":
    case "ends_with":
    case "contains":
      return (
        <input
          className="select"
          type="text"
          value={condition.value}
          disabled={readOnly}
          aria-label="value"
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
        />
      );
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return (
        <input
          className="select"
          type="number"
          value={condition.value}
          disabled={readOnly}
          aria-label="value"
          onChange={(e) => onChange({ ...condition, value: Number(e.target.value) })}
        />
      );
    case "between":
      return (
        <div className="row">
          <input
            className="select"
            type="number"
            value={condition.min}
            disabled={readOnly}
            aria-label="min"
            onChange={(e) => onChange({ ...condition, min: Number(e.target.value) })}
          />
          <input
            className="select"
            type="number"
            value={condition.max}
            disabled={readOnly}
            aria-label="max"
            onChange={(e) => onChange({ ...condition, max: Number(e.target.value) })}
          />
        </div>
      );
    case "semver_eq":
    case "semver_gte":
    case "semver_lte":
      return (
        <input
          className="select"
          type="text"
          value={condition.value}
          disabled={readOnly}
          placeholder="1.2.3"
          aria-label="value"
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
        />
      );
    case "semver_between":
      return (
        <div className="row">
          <input
            className="select"
            type="text"
            value={condition.min}
            disabled={readOnly}
            placeholder="min"
            aria-label="min"
            onChange={(e) => onChange({ ...condition, min: e.target.value })}
          />
          <input
            className="select"
            type="text"
            value={condition.max}
            disabled={readOnly}
            placeholder="max"
            aria-label="max"
            onChange={(e) => onChange({ ...condition, max: e.target.value })}
          />
        </div>
      );
    case "is_set":
    case "is_true":
    case "is_false":
      return <span className="muted">no value</span>;
    default:
      return null;
  }
}
